import { collection, addDoc, Timestamp, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { Notification, NotificationType, UserRole } from '../types';

/**
 * Register device for push notifications
 */
export const registerPushNotifications = async (userId: string): Promise<void> => {
    try {
        // Check if running in Capacitor environment
        if (typeof window === 'undefined' || !(window as any).Capacitor) {
            console.warn('PushNotifications plugin not available (not running in Capacitor)');
            return;
        }

        // Dynamically import PushNotifications
        let PushNotifications: any;
        try {
            // Use dynamic import - Vite will handle this at runtime
            // @ts-ignore - Capacitor plugins may not be available in dev environment
            const module = await import(/* @vite-ignore */ '@capacitor/push-notifications');
            PushNotifications = module.PushNotifications;
        } catch (e) {
            console.warn('PushNotifications plugin not available:', e);
            return;
        }

        if (!PushNotifications) {
            console.warn('PushNotifications plugin not available');
            return;
        }

        // Request permission
        let permStatus = await PushNotifications.checkPermissions();
        console.log('Initial permission status:', permStatus);
        
        if (permStatus.receive === 'prompt') {
            permStatus = await PushNotifications.requestPermissions();
            console.log('Permission request result:', permStatus);
        }
        
        if (permStatus.receive !== 'granted') {
            console.warn('Push notification permission not granted. Status:', permStatus.receive);
            console.warn('If notifications are blocked, please enable them in Android Settings > Apps > Arztaleb > Notifications');
            return;
        }

        // Register for push notifications
        await PushNotifications.register();

        // Listen for registration
        PushNotifications.addListener('registration', (token: any) => {
            console.log('Push registration success, token: ' + token.value);
            // Store token in Firestore for this user
            storePushToken(userId, token.value);
        });

        // Listen for errors
        PushNotifications.addListener('registrationError', (error: any) => {
            console.error('Error on registration: ' + JSON.stringify(error));
        });

        // Listen for push notifications
        PushNotifications.addListener('pushNotificationReceived', (notification: any) => {
            console.log('Push notification received: ', notification);
        });

        // Listen for push notification actions
        PushNotifications.addListener('pushNotificationActionPerformed', (notification: any) => {
            console.log('Push notification action performed', notification);
        });
    } catch (error) {
        console.error('Error registering push notifications:', error);
    }
};

/**
 * Store push token in Firestore
 */
const storePushToken = async (userId: string, token: string): Promise<void> => {
    try {
        const tokensRef = collection(db, 'pushTokens');
        const q = query(tokensRef, where('userId', '==', userId));
        const snapshot = await getDocs(q);
        
        if (snapshot.empty) {
            // Create new token document
            await addDoc(tokensRef, {
                userId,
                token,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });
        } else {
            // Update existing token
            const tokenDoc = snapshot.docs[0];
            await updateDoc(doc(db, 'pushTokens', tokenDoc.id), {
                token,
                updatedAt: Timestamp.now()
            });
        }
    } catch (error) {
        console.error('Error storing push token:', error);
    }
};

/**
 * Create a notification in Firestore
 */
export const createNotification = async (
    userId: string,
    type: NotificationType,
    title: string,
    message: string,
    relatedId?: string,
    data?: any
): Promise<string> => {
    try {
        console.log('Creating notification:', { userId, type, title, message });
        const notificationRef = await addDoc(collection(db, 'notifications'), {
            userId,
            type,
            title,
            message,
            read: false,
            createdAt: Timestamp.now(),
            relatedId: relatedId || null,
            data: data || null,
            archived: false
        });
        
        console.log('Notification created:', { notificationId: notificationRef.id, userId });
        return notificationRef.id;
    } catch (error) {
        console.error('Error creating notification:', error);
        throw error;
    }
};

/**
 * Send notification to multiple users
 */
export const sendNotificationToUsers = async (
    userIds: string[],
    type: NotificationType,
    title: string,
    message: string,
    relatedId?: string,
    data?: any
): Promise<void> => {
    try {
        console.log('Sending notifications to users:', { userIds, count: userIds.length, title, message });
        const promises = userIds.map(userId => 
            createNotification(userId, type, title, message, relatedId, data)
        );
        const results = await Promise.all(promises);
        console.log('Notifications created successfully:', { count: results.length, notificationIds: results });
    } catch (error) {
        console.error('Error sending notifications to users:', error);
        throw error;
    }
};

/**
 * Send notification to users by role
 */
export const sendNotificationToRole = async (
    users: any[],
    role: UserRole,
    type: NotificationType,
    title: string,
    message: string,
    relatedId?: string,
    data?: any
): Promise<void> => {
    try {
        const targetUsers = users.filter(u => u.role === role && !u.archived);
        const userIds = targetUsers.map(u => u.id);
        await sendNotificationToUsers(userIds, type, title, message, relatedId, data);
    } catch (error) {
        console.error('Error sending notifications to role:', error);
        throw error;
    }
};

/**
 * Send notification to office users
 */
export const sendNotificationToOffice = async (
    users: any[],
    officeId: string,
    type: NotificationType,
    title: string,
    message: string,
    relatedId?: string,
    data?: any
): Promise<void> => {
    try {
        const targetUsers = users.filter(
            u => (u.role === UserRole.Office || u.role === UserRole.Worker) && 
                 u.officeId === officeId && 
                 !u.archived
        );
        const userIds = targetUsers.map(u => u.id);
        await sendNotificationToUsers(userIds, type, title, message, relatedId, data);
    } catch (error) {
        console.error('Error sending notifications to office:', error);
        throw error;
    }
};
